// src/utils/passwordValidation.js

/**
 * Password validation utility functions
 */

export const PASSWORD_REQUIREMENTS = {
  MIN_LENGTH: 8,
  REQUIRE_LOWERCASE: false,
  REQUIRE_UPPERCASE: false,
  REQUIRE_NUMBER: false,
  REQUIRE_SPECIAL_CHAR: false
}

/**
 * Validate password against all requirements
 * @param {string} password - The password to validate
 * @returns {Object} - Validation result with isValid boolean and array of failed requirements
 */
export const validatePassword = (password) => {
  const failedRequirements = []
  
  if (!password) {
    return {
      isValid: false,
      failedRequirements: ['Password is required'],
      errors: ['Password is required']
    }
  }

  // Check minimum length
  if (password.length < PASSWORD_REQUIREMENTS.MIN_LENGTH) {
    failedRequirements.push(`Password must be at least ${PASSWORD_REQUIREMENTS.MIN_LENGTH} characters`)
  }

  return {
    isValid: failedRequirements.length === 0,
    failedRequirements,
    errors: failedRequirements
  }
}

/**
 * Get password strength score (0-100)
 * @param {string} password - The password to evaluate
 * @returns {number} - Strength score from 0 to 100
 */
export const getPasswordStrength = (password) => {
  if (!password) return 0

  let score = 0
  const checks = [
    password.length >= PASSWORD_REQUIREMENTS.MIN_LENGTH,
    /(?=.*[a-z])/.test(password),
    /(?=.*[A-Z])/.test(password),
    /(?=.*\d)/.test(password),
    /(?=.*[!@#$%^&*(),.?":{}|<>])/.test(password),
    password.length >= 12 // Bonus for longer passwords
  ]

  score = (checks.filter(Boolean).length / checks.length) * 100
  return Math.round(score)
}

/**
 * Get password strength text and color
 * @param {string} password - The password to evaluate
 * @returns {Object} - Object with text, color, and bgColor properties
 */
export const getPasswordStrengthInfo = (password) => {
  const strength = getPasswordStrength(password)
  
  if (strength < 30) {
    return {
      text: 'Weak',
      color: 'text-red-600',
      bgColor: 'bg-red-500',
      borderColor: 'border-red-300'
    }
  } else if (strength < 60) {
    return {
      text: 'Fair',
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-500',
      borderColor: 'border-yellow-300'
    }
  } else if (strength < 80) {
    return {
      text: 'Good',
      color: 'text-blue-600',
      bgColor: 'bg-blue-500',
      borderColor: 'border-blue-300'
    }
  } else {
    return {
      text: 'Strong',
      color: 'text-green-600',
      bgColor: 'bg-green-500',
      borderColor: 'border-green-300'
    }
  }
}

/**
 * Check individual password requirements
 * @param {string} password - The password to check
 * @returns {Object} - Object with boolean values for each requirement
 */
export const checkPasswordRequirements = (password) => {
  return {
    hasMinLength: password?.length >= PASSWORD_REQUIREMENTS.MIN_LENGTH,
    hasLowercase: /(?=.*[a-z])/.test(password || ''),
    hasUppercase: /(?=.*[A-Z])/.test(password || ''),
    hasNumber: /(?=.*\d)/.test(password || ''),
    hasSpecialChar: /(?=.*[!@#$%^&*(),.?":{}|<>])/.test(password || '')
  }
}